import { api } from '../api'
import type { components } from '../schema'

type S = components['schemas']

// Clients (BIBLE §19): who campaigns, seats and mailboxes, and prospect tables belong to.
export type Client = S['Client']
export type ClientStatus = S['ClientStatus']
export type ClientListing = S['ClientListing']
export type ListClientsResult = S['ListClientsResult']
export type SlugRules = S['SlugRules']
export type CreateClientBody = Omit<S['CreateClientRequest'], 'slug' | 'logo_url' | 'accent_color'> &
  Partial<Pick<S['CreateClientRequest'], 'slug' | 'logo_url' | 'accent_color'>>
export type CreateClientResult = S['CreateClientResult']
/** Partial: a field left out is left as it is; `logo_url`/`accent_color` null clears them. */
export type UpdateClientBody = S['UpdateClientBody']
export type UpdateClientResult = S['UpdateClientResult']
export type GetClientResult = S['GetClientResult']
export type AssignToClientBody = Partial<S['AssignBody']>
export type AssignToClientResult = S['AssignToClientResult']
export type MovedItem = S['MovedItem']
// Dashboard logins (BIBLE §19.5).
export type ClientUserListing = S['ClientUserListing']
export type ClientUserState = S['ClientUserState']
export type ListClientUsersResult = S['ListClientUsersResult']
export type ClientUserInviteResult = S['ClientUserInviteResult']
export type RemoveClientUserResult = S['RemoveClientUserResult']

export const clientsApi = api.injectEndpoints({
  endpoints: (build) => ({
    listClients: build.query<ListClientsResult, void>({
      query: () => '/clients',
      providesTags: [{ type: 'Client', id: 'LIST' }],
    }),

    getClient: build.query<GetClientResult, string>({
      query: (clientId) => `/clients/${clientId}`,
      providesTags: (_result, _error, clientId) => [{ type: 'Client', id: clientId }],
    }),

    createClient: build.mutation<CreateClientResult, CreateClientBody>({
      query: (body) => ({ url: '/clients', method: 'POST', body }),
      invalidatesTags: [{ type: 'Client', id: 'LIST' }],
    }),

    updateClient: build.mutation<UpdateClientResult, UpdateClientBody & { clientId: string }>({
      query: ({ clientId, ...body }) => ({ url: `/clients/${clientId}`, method: 'PATCH', body }),
      invalidatesTags: (_result, _error, { clientId }) => [
        { type: 'Client', id: 'LIST' },
        { type: 'Client', id: clientId },
      ],
    }),

    assignToClient: build.mutation<
      AssignToClientResult,
      AssignToClientBody & { clientId: string }
    >({
      // A move changes whose every list a campaign, seat or table appears in, and the
      // counts on every client, so everything those lists draw is re-read.
      query: ({ clientId, ...body }) => ({
        url: `/clients/${clientId}/assign`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Client', 'Campaign', 'Account', 'Table', 'Conversation', 'EmailOverview'],
    }),

    listClientUsers: build.query<ListClientUsersResult, string>({
      query: (clientId) => `/clients/${clientId}/users`,
      providesTags: (_result, _error, clientId) => [{ type: 'ClientUser', id: clientId }],
    }),

    addClientUser: build.mutation<ClientUserInviteResult, { clientId: string; email: string }>({
      query: ({ clientId, email }) => ({
        url: `/clients/${clientId}/users`,
        method: 'POST',
        body: { email },
      }),
      invalidatesTags: (_result, _error, { clientId }) => [{ type: 'ClientUser', id: clientId }],
    }),

    reissueClientInvite: build.mutation<
      ClientUserInviteResult,
      { clientId: string; userId: string }
    >({
      query: ({ clientId, userId }) => ({
        url: `/clients/${clientId}/users/${userId}/invite`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, { clientId }) => [{ type: 'ClientUser', id: clientId }],
    }),

    removeClientUser: build.mutation<RemoveClientUserResult, { clientId: string; userId: string }>({
      query: ({ clientId, userId }) => ({
        url: `/clients/${clientId}/users/${userId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { clientId }) => [{ type: 'ClientUser', id: clientId }],
    }),
  }),
})

export const {
  useListClientsQuery,
  useGetClientQuery,
  useCreateClientMutation,
  useUpdateClientMutation,
  useAssignToClientMutation,
  useListClientUsersQuery,
  useAddClientUserMutation,
  useReissueClientInviteMutation,
  useRemoveClientUserMutation,
} = clientsApi
